import { East, NotificationsNone } from '@mui/icons-material'
import { Box, Divider, Typography, styled } from '@mui/material'
import { navigate } from 'gatsby'
import React from 'react'
import { HBox, VBox } from '../../../elements/basic-elements'
import type { UserNotification } from '../../../async/supabase/notifications'
import { colors } from '../../../constants/colors'
import profilePlaceholder from '../../../images/profile-placeholder.png'
import { Avatar } from '../../user-avatar'

export type NotificationRowProps = {
  notification: UserNotification
  onRead: () => void
  onNavigate?: () => void
}

const AVATAR_SIZE = 60

export const NotificationRowShell = ({
  notification,
  onRead,
  onNavigate,
  avatar,
  destination,
  highlights
}: NotificationRowProps & {
  /** Left-hand visual — use <UserAvatarCircle /> or <ImageCircle /> */
  avatar?: React.ReactNode
  /** Where the row (and its arrow) takes the user */
  destination?: string
  /** Names within the body to render in bold (e.g. the friend or action name) */
  highlights?: (string | undefined)[]
}) => {
  const handleClick = () => {
    if (!notification.read) onRead()
    if (destination) {
      onNavigate?.()
      navigate(destination)
    }
  }

  const clickable = Boolean(destination) || !notification.read

  return (
    <>
      <HBox
        px={2.5}
        py={2.5}
        gap={2}
        alignItems="flex-start"
        onClick={handleClick}
        role={destination ? 'link' : undefined}
        sx={{
          cursor: clickable ? 'pointer' : 'default',
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background-color 0.15s'
        }}
      >
        <Box flexShrink={0}>{avatar || <IconCircle />}</Box>

        <VBox flex={1} gap={0.5} minWidth={0}>
          <Typography variant="body1" fontWeight={500} color="textPrimary">
            {notification.title}
          </Typography>
          {notification.body && (
            <Typography variant="body2" color="textSecondary">
              <HighlightedText text={notification.body} highlights={highlights} />
            </Typography>
          )}
          <HBox alignItems="center" gap={0.75} mt={0.5}>
            {!notification.read && <UnreadDot />}
            <NotificationTimestamp notification={notification} />
          </HBox>
        </VBox>

        {destination && (
          <East fontSize="small" sx={{ flexShrink: 0, mt: 0.25, color: 'text.primary' }} />
        )}
      </HBox>
      <Divider />
    </>
  )
}

// ─── Avatars ─────────────────────────────────────────────────────────────────

export const UserAvatarCircle = ({ src }: { src?: string }) => (
  <Avatar src={src || profilePlaceholder} size={AVATAR_SIZE} style={{ borderRadius: '50%' }} />
)

export const ImageCircle = ({ src, alt }: { src?: string; alt?: string }) =>
  src ? (
    <CircleFrame>
      <Box
        component="img"
        src={src}
        alt={alt || ''}
        sx={{ width: '70%', height: '70%', objectFit: 'contain' }}
      />
    </CircleFrame>
  ) : (
    <IconCircle />
  )

const IconCircle = () => (
  <CircleFrame>
    <NotificationsNone sx={{ color: 'text.secondary' }} />
  </CircleFrame>
)

const CircleFrame = styled(Box)`
  width: ${AVATAR_SIZE}px;
  height: ${AVATAR_SIZE}px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.action.hover};
`

// ─── Body text ───────────────────────────────────────────────────────────────

/** Bolds the first occurrence of each highlight string found in the text. */
const HighlightedText = ({
  text,
  highlights
}: {
  text: string
  highlights?: (string | undefined)[]
}) => {
  const terms = (highlights || []).filter((h): h is string => Boolean(h && h.trim()))
  if (terms.length === 0) return <>{text}</>

  const ranges: [number, number][] = []
  terms.forEach(term => {
    const start = text.indexOf(term)
    if (start === -1) return
    const end = start + term.length
    if (ranges.some(([s, e]) => start < e && end > s)) return
    ranges.push([start, end])
  })
  ranges.sort((a, b) => a[0] - b[0])

  const parts: React.ReactNode[] = []
  let cursor = 0
  ranges.forEach(([start, end]) => {
    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(
      <Box component="strong" key={start} sx={{ color: 'text.primary', fontWeight: 600 }}>
        {text.slice(start, end)}
      </Box>
    )
    cursor = end
  })
  if (cursor < text.length) parts.push(text.slice(cursor))
  return <>{parts}</>
}

// ─── Timestamp ───────────────────────────────────────────────────────────────

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export const formatNotificationTime = (createdAt?: string, now = Date.now()) => {
  if (!createdAt) return ''
  const date = new Date(createdAt)
  const elapsed = now - date.getTime()
  if (Number.isNaN(elapsed)) return ''
  if (elapsed < MINUTE) return 'Just now'
  if (elapsed < HOUR) {
    const minutes = Math.floor(elapsed / MINUTE)
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  }
  if (elapsed < DAY) {
    const hours = Math.floor(elapsed / HOUR)
    return `${hours} hour${hours === 1 ? '' : 's'} ago`
  }
  const sameYear = date.getFullYear() === new Date(now).getFullYear()
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' })
  })
}

const NotificationTimestamp = ({ notification }: { notification: UserNotification }) => (
  <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
    {formatNotificationTime(notification.created_at)}
  </Typography>
)

export const UnreadDot = styled(Box)`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${colors.RED_100};
  flex-shrink: 0;
`
